import { v4 } from 'uuid';
import ls from 'localstorage-slim';
import type { PlatformTournaments, TournamentCreateConfig, TournamentData } from '@hexa/app/platform/Platform';
import { MS_IN_DAY } from '@hexa/utils/time-utils';
import { isNullish } from '@softgames/type-guards';

export class FakeTournaments implements PlatformTournaments {
  private static readonly localStorageKey = 'hexa-dev-fakeTournaments';

  private static readonly localStorageEntryKey = 'hexa-dev-fakeTournamentsEntry';

  private static readonly localStorageContextKey = 'hexa-dev-fakeTournamentsContext';

  private currentTournament: TournamentData | null = null;

  private currentContext: string | null = null;

  private tournaments: Record<string, TournamentData> = {};

  public static debugClearTournaments(): void {
    ls.remove(this.localStorageKey);
  }

  public static setEntryTournamentForNextSession(): void {
    ls.set(FakeTournaments.localStorageEntryKey, true);
  }

  constructor() {
    const savedTournament = ls.get<TournamentData>(FakeTournaments.localStorageKey);
    if (savedTournament) {
      this.tournaments[savedTournament.tournamentId] = savedTournament;
      // Store the localization key; it is localized at display time so it follows
      // the current language instead of being frozen to the language at creation.
      this.tournaments[savedTournament.tournamentId].title = 'Tournament';
      ls.set(FakeTournaments.localStorageKey, this.tournaments[savedTournament.tournamentId]);
    }

    const savedContextId = ls.get<string>(FakeTournaments.localStorageContextKey);
    if (savedContextId) {
      this.changeContext(savedContextId);
    }

    this.fetchEntryTournament();
  }

  private changeContext(contextId: string | null): void {
    this.currentContext = contextId;
    this.currentTournament =
      Object.values(this.tournaments).find((tournament) => tournament.contextId === contextId) ?? null;
  }

  private fetchEntryTournament(): void {
    if (!ls.get(FakeTournaments.localStorageEntryKey)) return;

    this.currentTournament = this.createFakeTournamentData({
      initialScore: 0,
      config: {
        title: 'Tournament',
      },
      data: {
        creatorId: v4(),
      },
    });
    this.currentContext = this.currentTournament.contextId;

    ls.remove(FakeTournaments.localStorageEntryKey);
  }

  private createFakeTournamentData(createConfig: TournamentCreateConfig): TournamentData {
    const tournamentData = {
      contextId: v4(),
      creatorId: (createConfig.data as Record<string, string>).tournamentCreatorId,
      endsAt: Date.now() + MS_IN_DAY * 7,
      payload: createConfig.data,
      title: 'Tournament',
      tournamentId: v4(),
    };

    this.tournaments[tournamentData.tournamentId] = tournamentData;
    ls.set(FakeTournaments.localStorageKey, tournamentData);
    ls.set(FakeTournaments.localStorageContextKey, tournamentData.contextId);

    this.currentTournament = tournamentData;

    return tournamentData;
  }

  public async createTournament(config: TournamentCreateConfig): Promise<TournamentData | null> {
    this.currentTournament = this.createFakeTournamentData(config);
    this.currentContext = this.currentTournament.contextId;
    return this.currentTournament;
  }

  public async shareTournament(_score: number, _payload: Record<string, unknown>): Promise<boolean> {
    return true;
  }

  public async getCurrentTournament(): Promise<TournamentData | null> {
    return this.currentTournament;
  }

  public async joinTournament(tournamentId: string): Promise<TournamentData | null> {
    const tournamentData = this.tournaments[tournamentId];
    if (isNullish(tournamentData)) return null;
    this.currentTournament = tournamentData;
    this.currentContext = tournamentData.contextId;
    return tournamentData;
  }

  public async postScore(_score: number): Promise<boolean> {
    return true;
  }

  public getContextID(): string | null {
    return this.currentContext;
  }
}
